// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import actionsOrganizations from '../../../api/actionsOrganizations';
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useOrganizationsStore from '@/app/store/modules/organizations';
// Components
import ModalSendMail from '@/common-utils/components/renders/modal/components/modals/ModalSendMail';
import Entity from '@/common-utils/components/renders/entity/Entity';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
import AlertEmailTypes from '../../../enums/AlertEmailTypes';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.type
 * @returns {JSX.Element} ModalSendMail
*/
function ModalAddAlertsEmail({ isOpen, setIsOpen, type }) {

    // Store
    const { alertReceivers, simAlerts, storeRefetchAlertReceivers, storeRefetchSimAlerts } = useOrganizationsStore();
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);

    // Constants
    const MAX_EMAILS_TO_SEND = 5;
    const maxEmailsNumber = type === AlertEmailTypes.NEW_POS_EMAILS.name ?
        MAX_EMAILS_TO_SEND - alertReceivers.length : MAX_EMAILS_TO_SEND - simAlerts.mailList.length;
    const titleEntity = type === AlertEmailTypes.NEW_POS_EMAILS.name ? 'modal.add_emails.title_new_pos' : 'modal.add_emails.title_consumption';
    const isActionBlocked = (type === AlertEmailTypes.SIM_EMAILS.name) && !simAlerts.isActiveSimCardAlert && !simAlerts.isActiveSimPoolAlert
    const isMaxEmailsNumberReached = type === AlertEmailTypes.NEW_POS_EMAILS.name ?
        alertReceivers.length >= MAX_EMAILS_TO_SEND : simAlerts.mailList.length >= MAX_EMAILS_TO_SEND;

    // State
    const [renderList, setRenderList] = useState([{ email: '', isValid: true }]);

    // Toast
    const { toast } = useToast();

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        if (type === AlertEmailTypes.NEW_POS_EMAILS.name) {
            storeRefetchAlertReceivers(true);
        }
        else {
            storeRefetchSimAlerts(true);
        }
        toast({
            description: <Entity entity='modal.add_emails.success' namespace={nsOrganization} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.errors.UNKNOWN_ERROR' namespace={nsOrganization} />,
            variant: 'danger'
        });
    };
    const addNewPosEmails = actionsOrganizations.addNewPosEmails(onSuccess, onError);
    const addSimEmails = actionsOrganizations.addSimEmails(onSuccess, onError);

    // Functions
    const validateSendingMail = () => {
        const recipientsList = renderList.map(user => user.email);

        if (type === AlertEmailTypes.NEW_POS_EMAILS.name) {
            addNewPosEmails.mutate({
                organizationId: rootOrganization.uuid,
                emails: recipientsList
            });
        }
        else {
            addSimEmails.mutate({
                organizationId: rootOrganization.uuid,
                emails: recipientsList
            });
        }
    };

    return (
        <ModalSendMail
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            isBlocked={isActionBlocked || isMaxEmailsNumberReached}
            blockedAction={{
                message: isMaxEmailsNumberReached ? 'modal.add_emails.max_emails_number_reached' : 'modal.add_emails.blocked',
                namespace: nsOrganization,
                data: { emailsNumber: MAX_EMAILS_TO_SEND }
            }}
            onValidate={validateSendingMail}
            isLoading={addNewPosEmails.isLoading || addSimEmails.isLoading}
            header={{ title: titleEntity, namespace: nsOrganization }}
            message={{ entity: 'modal.add_emails.message', namespace: nsOrganization, data: { emailsNumber: maxEmailsNumber } }}
            confirm={{ entity: 'modal.add_emails.confirm', namespace: nsOrganization }}
            renderList={renderList}
            setRenderList={setRenderList}
            maxEmailsNumber={maxEmailsNumber}
            existingEmails={type === AlertEmailTypes.NEW_POS_EMAILS.name ? alertReceivers : simAlerts.mailList}
        />
    );
}

ModalAddAlertsEmail.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    type: PropTypes.string
};

export default ModalAddAlertsEmail;